
import React, { useState, useEffect, useCallback, useRef } from "react";
import * as XLSX from "xlsx";
import { URL } from "../config";

const API_URL        = `${URL}/enquiries`;
const EMP_API_URL    = `${URL}/employee-masters`;
const BUDGET_API_URL = `${URL}/budget-masters`;
const AREA_API_URL   = `${URL}/area-masters`;
const STATUS_API_URL = `${URL}/lead-status-masters`;
const SOURCE_API_URL = `${URL}/source-masters`;
const PROP_API_URL   = `${URL}/property-type-masters`;
const FOLLOWUP_API_URL = `${URL}/followup-masters`;

if (typeof document !== "undefined" && !document.getElementById("poppins-font")) {
  const link = document.createElement("link");
  link.id   = "poppins-font";
  link.rel  = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap";
  document.head.appendChild(link);
}

const BR = "#1D4ED8";
const DK = "#072877";

const AVATAR_COLORS = [
  "#1D4ED8","#7C3AED","#DB2777","#D97706","#059669",
  "#DC2626","#2563EB","#9333EA","#EA580C","#0891B2",
];
const getAvatarColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};
const getInitials = (name) => {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getStatusConfig = (statusName) => {
  if (!statusName) return { bg: "#F5F3FF", color: "#6D28D9", icon: "🆕", label: "New" };
  const n = statusName.toLowerCase();
  if (n.includes("follow"))  return { bg: "#FEF9C3", color: "#854D0E", icon: "🔁",  label: statusName };
  if (n.includes("success")) return { bg: "#DCFCE7", color: "#166534", icon: "✅",  label: statusName };
  if (n.includes("cancel"))  return { bg: "#FEE2E2", color: "#991B1B", icon: "❌",  label: statusName };
  return { bg: "#F5F3FF", color: "#6D28D9", icon: "🆕", label: statusName };
};

function Avatar({ name, size = 32 }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: size, height: size, borderRadius: "50%",
      background: getAvatarColor(name), color: "#fff",
      fontSize: size * 0.38, fontWeight: 700, flexShrink: 0, lineHeight: 1,
      fontFamily: "'Poppins', sans-serif",
    }}>
      {getInitials(name)}
    </span>
  );
}

function StatusBadge({ statusName }) {
  const cfg = getStatusConfig(statusName);
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      padding: "3px 10px", borderRadius: 20, fontSize: 12,
      fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4,
      fontFamily: "'Poppins', sans-serif", whiteSpace: "nowrap",
    }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

function Skeleton({ width = "100%", height = 16, radius = 6 }) {
  return (
    <div style={{
      width, height, borderRadius: radius,
      background: "linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 1.4s infinite",
    }} />
  );
}

// ── Employee Single-Select Dropdown ───────────────────────────────────────
function EmployeeDropdown({ label = "Employee", employees, selectedId, onChange }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState("");
  const ref                 = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered  = employees.filter(emp =>
    (emp.name || "").toLowerCase().includes(search.toLowerCase()) ||
    (emp.desig || "").toLowerCase().includes(search.toLowerCase())
  );
  const selected  = selectedId ? employees.find(e => e.id === selectedId) : null;
  const isActive  = !!selectedId;
  const btnLabel  = selected ? selected.name : `All ${label}s`;

  const handleSelect = (id) => { onChange(id === selectedId ? null : id); setOpen(false); setSearch(""); };

  return (
    <div ref={ref} style={{ position: "relative", display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: "#374151", letterSpacing: "0.5px", textTransform: "uppercase" }}>{label}</label>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          height: 42, padding: "0 36px 0 12px", borderRadius: 8,
          border: `1.5px solid ${isActive ? BR : "#E2E8F0"}`,
          background: "#fff", cursor: "pointer",
          fontFamily: "'Poppins', sans-serif",
          color: isActive ? BR : "#64748B",
          fontWeight: isActive ? 700 : 500,
          textAlign: "left", minWidth: 190,
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
          backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center",
          display: "flex", alignItems: "center", gap: 8, overflow: "hidden",
          fontSize: 13,
        }}
      >
        {isActive
          ? <><Avatar name={selected?.name} size={22} /><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{btnLabel}</span></>
          : <><span style={{ fontSize: 15, flexShrink: 0 }}>👤</span><span>{btnLabel}</span></>
        }
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 1000,
          background: "#fff", borderRadius: 10, border: "1.5px solid #E2E8F0",
          minWidth: 260, boxShadow: "0 8px 24px rgba(0,0,0,.13)", overflow: "hidden",
        }}>
          <div style={{ padding: "8px 10px", borderBottom: "1px solid #F1F5F9" }}>
            <input autoFocus type="text" placeholder={`Search ${label.toLowerCase()}...`} value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: "100%", height: 32, border: "1.5px solid #E2E8F0", borderRadius: 6, padding: "0 8px", fontSize: 12.5, fontFamily: "'Poppins', sans-serif", outline: "none", boxSizing: "border-box", color: "#1E293B" }}
            />
          </div>
          <div onClick={() => handleSelect(null)}
            style={{ display: "flex", alignItems: "center", gap: 9, padding: "9px 12px", cursor: "pointer", background: !selectedId ? "#EFF6FF" : "transparent", borderBottom: "1px solid #F1F5F9" }}
            onMouseEnter={e => { if (selectedId) e.currentTarget.style.background = "#F8FAFC"; }}
            onMouseLeave={e => { if (selectedId) e.currentTarget.style.background = "transparent"; }}
          >
            <span style={{ fontSize: 16 }}>👥</span>
            <span style={{ fontSize: 13, fontWeight: !selectedId ? 700 : 500, color: !selectedId ? BR : "#1E293B", fontFamily: "'Poppins', sans-serif" }}>All {label}s</span>
            {!selectedId && <span style={{ marginLeft: "auto", fontSize: 11, color: BR, fontWeight: 700 }}>✓</span>}
          </div>
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {filtered.length === 0
              ? <div style={{ padding: 14, fontSize: 12, color: "#94A3B8", textAlign: "center" }}>No results</div>
              : filtered.map(emp => {
                const isSel = selectedId === emp.id;
                return (
                  <div key={emp.id} onClick={() => handleSelect(emp.id)}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", cursor: "pointer", background: isSel ? "#EFF6FF" : "transparent", borderBottom: "1px solid #F8FAFC" }}
                    onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = "#F8FAFC"; }}
                    onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = "transparent"; }}
                  >
                    <Avatar name={emp.name} size={28} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: isSel ? 700 : 500, color: isSel ? BR : "#1E293B", fontFamily: "'Poppins', sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{emp.name}</div>
                      {emp.desig && <div style={{ fontSize: 11, color: "#94A3B8", fontFamily: "'Poppins', sans-serif" }}>{emp.desig}</div>}
                    </div>
                    {isSel && <span style={{ fontSize: 11, color: BR, fontWeight: 700, flexShrink: 0 }}>✓</span>}
                  </div>
                );
              })
            }
          </div>
        </div>
      )}
    </div>
  );
}

// ── Area Multi-Select Dropdown ─────────────────────────────────────────────
function AreaMultiSelect({ areas, selectedIds, onChange }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState("");
  const ref                 = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filteredAreas = areas.filter(a => (a.areaName || "").toLowerCase().includes(search.toLowerCase()));
  const toggle = (id) => onChange(selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id]);
  const isActive = selectedIds.length > 0;
  const label = !isActive ? "All Areas"
    : selectedIds.length === 1 ? (areas.find(a => a.areaId === selectedIds[0])?.areaName || "1 selected")
    : `${selectedIds.length} areas selected`;

  return (
    <div ref={ref} style={{ position: "relative", display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: "#374151", letterSpacing: "0.5px", textTransform: "uppercase" }}>Area</label>
      <button onClick={() => setOpen(o => !o)} style={{
        height: 42, padding: "0 36px 0 14px", borderRadius: 8,
        border: `1.5px solid ${isActive ? BR : "#E2E8F0"}`,
        background: "#fff", cursor: "pointer", fontSize: 13,
        fontFamily: "'Poppins', sans-serif",
        color: isActive ? BR : "#64748B", fontWeight: isActive ? 700 : 500,
        textAlign: "left", minWidth: 170,
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
        backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>{label}</button>

      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 1000, background: "#fff", borderRadius: 10, border: "1.5px solid #E2E8F0", minWidth: 230, boxShadow: "0 8px 24px rgba(0,0,0,.13)", overflow: "hidden" }}>
          <div style={{ padding: "8px 10px", borderBottom: "1px solid #F1F5F9" }}>
            <input autoFocus type="text" placeholder="Search area..." value={search} onChange={e => setSearch(e.target.value)}
              style={{ width: "100%", height: 32, border: "1.5px solid #E2E8F0", borderRadius: 6, padding: "0 8px", fontSize: 12.5, fontFamily: "'Poppins', sans-serif", outline: "none", boxSizing: "border-box", color: "#1E293B" }}
            />
          </div>
          <div style={{ padding: "6px 12px", borderBottom: "1px solid #F1F5F9", display: "flex", gap: 10 }}>
            <button onClick={() => onChange(areas.map(a => a.areaId))} style={{ fontSize: 11.5, fontWeight: 700, color: BR, background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "'Poppins', sans-serif" }}>Select All</button>
            <span style={{ color: "#E2E8F0", alignSelf: "center" }}>|</span>
            <button onClick={() => onChange([])} style={{ fontSize: 11.5, fontWeight: 700, color: "#991B1B", background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "'Poppins', sans-serif" }}>Clear</button>
          </div>
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            {filteredAreas.length === 0
              ? <div style={{ padding: 14, fontSize: 12, color: "#94A3B8", textAlign: "center" }}>No areas found</div>
              : filteredAreas.map(a => {
                const checked = selectedIds.includes(a.areaId);
                return (
                  <div key={a.areaId} onClick={() => toggle(a.areaId)}
                    style={{ display: "flex", alignItems: "center", gap: 9, padding: "9px 12px", cursor: "pointer", background: checked ? "#EFF6FF" : "transparent", borderBottom: "1px solid #F8FAFC" }}
                    onMouseEnter={e => { if (!checked) e.currentTarget.style.background = "#F8FAFC"; }}
                    onMouseLeave={e => { if (!checked) e.currentTarget.style.background = "transparent"; }}
                  >
                    <span style={{ width: 16, height: 16, borderRadius: 4, flexShrink: 0, border: `2px solid ${checked ? BR : "#CBD5E1"}`, background: checked ? BR : "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                      {checked && <span style={{ color: "#fff", fontSize: 10, fontWeight: 900, lineHeight: 1 }}>✓</span>}
                    </span>
                    <span style={{ fontSize: 13, color: checked ? BR : "#1E293B", fontWeight: checked ? 600 : 400, fontFamily: "'Poppins', sans-serif" }}>{a.areaName}</span>
                  </div>
                );
              })
            }
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Enquiry Detail Modal ─────────────────────────────────────────────────────
function EnquiryModal({ enquiry, onClose, empMap, lsMap }) {
  useEffect(() => {
    if (!enquiry) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose, enquiry]);

  if (!enquiry) return null;
  const c = enquiry.customer || {};
  const latestFollowup = enquiry.followups?.[0] || null;
  const statusName = latestFollowup?.fkLeadStatusId ? (lsMap[latestFollowup.fkLeadStatusId] || "—") : "New";
  const assignName = latestFollowup?.assignTo ? (empMap[latestFollowup.assignTo]?.name || `Emp #${latestFollowup.assignTo}`) : null;

  const rows = [
    { label: "Enquiry ID",    value: enquiry.enquiryId },
    { label: "Enquiry Date",  value: enquiry.enquiryDate ? new Date(enquiry.enquiryDate).toLocaleDateString("en-IN") : "—" },
    { label: "Customer",      value: c.customerName || "—" },
    { label: "Mobile",        value: c.mobileno || "—" },
    { label: "Email",         value: c.emailid || "—" },
    { label: "City",          value: c.city || "—" },
    { label: "Source",        value: enquiry.source?.sourceName || "—" },
    { label: "Property Type", value: enquiry.propertyType?.propertytypeName || "—" },
    { label: "Budget",        value: enquiry.budget?.budgetName || "—" },
    { label: "Primary Goal",  value: enquiry.primaryGoal?.primarygoalName || "—" },
    { label: "Timeline",      value: enquiry.timeline?.timelineName || "—" },
    { label: "Employee Ref.", value: enquiry.employee?.employeeName || "—" },
    { label: "Comm. Media",   value: enquiry.communicationMedia?.communicationmediaName || "—" },
    { label: "Areas",         value: enquiry.areas?.map(a => a.areaName).join(", ") || "—" },
    { label: "Note",          value: enquiry.note || "—" },
  ];

  const followups = Array.isArray(enquiry.followups) ? enquiry.followups : [];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(7,40,119,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 16, backdropFilter: "blur(4px)" }}>
      <div style={{ background: "#fff", width: "100%", maxWidth: 660, borderRadius: 16, overflow: "hidden", boxShadow: "0 24px 60px rgba(7,40,119,.2)", display: "flex", flexDirection: "column", maxHeight: "92vh", fontFamily: "'Poppins', sans-serif" }}>
        {/* Header */}
        <div style={{ background: `linear-gradient(120deg,${DK},${BR})`, padding: "16px 20px", color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Avatar name={c.customerName} size={40} />
            <div>
              <div style={{ fontWeight: 800, fontSize: 15 }}>{c.customerName || "Unknown"}</div>
              <div style={{ fontSize: 12, opacity: .75 }}>Enquiry #{enquiry.enquiryId}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,.15)", border: "none", color: "#fff", width: 30, height: 30, borderRadius: "50%", cursor: "pointer", fontSize: 16 }}>✕</button>
        </div>

        <div style={{ overflowY: "auto", padding: "18px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #F1F5F9" }}>
                  <td style={{ padding: "8px 10px 8px 0", fontWeight: 600, color: "#64748B", whiteSpace: "nowrap", width: "38%", verticalAlign: "top" }}>{r.label}</td>
                  <td style={{ padding: "8px 0", color: "#1E293B", wordBreak: "break-word" }}>{r.value}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {latestFollowup && (
            <div style={{ background: "#EFF6FF", borderLeft: `4px solid ${BR}`, borderRadius: "0 8px 8px 0", padding: "12px 16px" }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: BR, marginBottom: 8 }}>Latest Follow-up</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", fontSize: 13, color: "#475569" }}>
                <span><strong>Status:</strong> <StatusBadge statusName={statusName} /></span>
                {assignName && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <strong>Assigned To:</strong>
                    <Avatar name={assignName} size={20} />
                    <span style={{ fontWeight: 600, color: "#1E293B" }}>{assignName}</span>
                  </span>
                )}
                {latestFollowup.callLeadDate && <span><strong>Follow-up Date:</strong> {new Date(latestFollowup.callLeadDate).toLocaleDateString("en-IN")}</span>}
                {latestFollowup.callLeadTime && <span><strong>Time:</strong> {latestFollowup.callLeadTime}</span>}
              </div>
            </div>
          )}

          {/* Full Follow-up History */}
          {followups.length > 1 && (
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#64748B", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                🕐 Follow-up History
                <span style={{ background: "#EFF6FF", color: BR, borderRadius: 20, padding: "1px 9px", fontSize: 11.5, fontWeight: 700 }}>{followups.length}</span>
              </div>
              <div style={{ borderRadius: 8, overflow: "hidden", border: "1px solid #F1F5F9" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: DK, borderBottom: `2px solid ${BR}` }}>
                      {["#", "Date", "Time", "Status", "Assigned To", "Remark"].map(h => (
                        <th key={h} style={{ padding: "8px 10px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#ffffff", letterSpacing: "0.5px", textTransform: "uppercase", fontFamily: "'Poppins', sans-serif" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {followups.map((fu, i) => {
                      const fuStatusName = fu.fkLeadStatusId ? (lsMap[fu.fkLeadStatusId] || "—") : "—";
                      const fuAssignName = fu.assignTo ? (empMap[fu.assignTo]?.name || `Emp #${fu.assignTo}`) : "—";
                      return (
                        <tr key={i} style={{ borderBottom: "1px solid #F8FAFC" }}>
                          <td style={{ padding: "8px 10px", fontSize: 12, color: "#94A3B8", fontWeight: 600 }}>{i + 1}</td>
                          <td style={{ padding: "8px 10px", fontSize: 12, color: "#475569", whiteSpace: "nowrap" }}>
                            {fu.callLeadDate ? new Date(fu.callLeadDate).toLocaleDateString("en-IN") : "—"}
                          </td>
                          <td style={{ padding: "8px 10px", fontSize: 12, color: "#475569", whiteSpace: "nowrap" }}>{fu.callLeadTime || "—"}</td>
                          <td style={{ padding: "8px 10px" }}><StatusBadge statusName={fuStatusName} /></td>
                          <td style={{ padding: "8px 10px", fontSize: 12, color: "#1E293B", fontWeight: 600, whiteSpace: "nowrap" }}>{fuAssignName}</td>
                          <td style={{ padding: "8px 10px", fontSize: 12, color: "#475569", fontStyle: "italic", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fu.followupRemark || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: "12px 20px", borderTop: "1px solid #E2E8F0", background: "#F8FAFC", display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "9px 24px", borderRadius: 50, border: "none", background: BR, color: "#fff", fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: "'Poppins', sans-serif" }}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────
const formatDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const getAreaLabel = (enq) => {
  if (!enq?.areas) return null;
  return enq.areas.map(a => a.areaName || a.name || "").filter(Boolean).join(", ") || null;
};

const getAreaIds = (enq) => {
  if (!enq?.areas) return [];
  return enq.areas.map(a => a.areaId).filter(Boolean);
};

const PER_PAGE = 10;

// ── Main Component ─────────────────────────────────────────────────────────
export default function EnquiryReport() {
  const [allData, setAllData]     = useState([]);
  const [followupData, setFollowupData] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [createdByFilter, setCreatedByFilter]   = useState(null);
  const [assignedToFilter, setAssignedToFilter] = useState(null);
  const [selectedAreas, setSelectedAreas]       = useState([]);
  const [statusFilter, setStatusFilter]         = useState("All");
  const [propertyFilter, setPropertyFilter]     = useState("All");
  const [sourceFilter, setSourceFilter]         = useState("All");
  const [startDate, setStartDate]               = useState("");
  const [endDate, setEndDate]                   = useState("");
  const [searchQuery, setSearchQuery]           = useState("");
  const [page, setPage]                         = useState(1);
  const [selectedEnquiry, setSelectedEnquiry]   = useState(null);

  // Reference data
  const [empMap, setEmpMap]         = useState({});
  const [empList, setEmpList]       = useState([]);
  const [lsMap, setLsMap]           = useState({});
  const [areaList, setAreaList]     = useState([]);
  const [propTypes, setPropTypes]   = useState([]);
  const [sources, setSources]       = useState([]);

  // ── Fetch all data ─────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const af  = encodeURIComponent(JSON.stringify({ where: { isActive: 1 } }));
      const efFields = encodeURIComponent(JSON.stringify({ where: { isActive: 1 }, fields: ["employeeId","employeeName","designation"] }));
      const enqFilter = encodeURIComponent(JSON.stringify({ where: { isActive: 1 }, order: ["enquiryId DESC"] }));
      const fuFilter  = encodeURIComponent(JSON.stringify({ where: { isActive: 1 }, order: ["followupId DESC"] }));
      const areaFilter = encodeURIComponent(JSON.stringify({ where: { isActive: 1 }, order: ["areaName ASC"] }));

      const [enqRes, fuRes, empRes, lsRes, areaRes, propRes, srcRes] = await Promise.all([
        fetch(`${API_URL}?filter=${enqFilter}`),
        fetch(`${FOLLOWUP_API_URL}?filter=${fuFilter}`),
        fetch(`${EMP_API_URL}?filter=${efFields}`),
        fetch(`${STATUS_API_URL}?filter=${af}`),
        fetch(`${AREA_API_URL}?filter=${areaFilter}`),
        fetch(`${PROP_API_URL}?filter=${af}`),
        fetch(`${SOURCE_API_URL}?filter=${af}`),
      ]);

      if (!enqRes.ok) throw new Error(`Server error ${enqRes.status}`);

      const [enqData, fuData, empData, lsData, areaData, propData, srcData] = await Promise.all([
        enqRes.json(),
        fuRes.ok ? fuRes.json() : [],
        empRes.ok ? empRes.json() : [],
        lsRes.ok  ? lsRes.json()  : [],
        areaRes.ok ? areaRes.json() : [],
        propRes.ok ? propRes.json() : [],
        srcRes.ok  ? srcRes.json()  : [],
      ]);

      // Employee map
      const eMap = {}; const eList = [];
      (Array.isArray(empData) ? empData : []).forEach(e => {
        const id   = e.employeeId ?? e.id;
        const name = e.employeeName ?? e.name ?? `Emp #${id}`;
        const desig = e.designation ?? e.designationName ?? "";
        if (id != null) { eMap[id] = { name, desig }; eList.push({ id, name, desig }); }
      });
      setEmpMap(eMap);
      eList.sort((a, b) => a.name.localeCompare(b.name));
      setEmpList(eList);

      // Lead status map
      const lMap = {};
      (Array.isArray(lsData) ? lsData : []).forEach(l => {
        const id = l.statusId ?? l.leadStatusId ?? l.id;
        const name = l.statusName ?? l.leadStatusName ?? l.name ?? "";
        if (id != null) lMap[id] = name;
      });
      setLsMap(lMap);

      // Area list
      setAreaList((Array.isArray(areaData) ? areaData : []).map(a => ({
        areaId: a.areaId ?? a.id,
        areaName: a.areaName ?? a.name ?? `Area #${a.areaId ?? a.id}`,
      })));

      // Property types
      setPropTypes(Array.isArray(propData) ? propData : []);

      // Sources
      setSources(Array.isArray(srcData) ? srcData : []);

      // Merge followups into enquiries
      const fuArr = Array.isArray(fuData) ? fuData : [];
      setFollowupData(fuArr);

      const withFollowups = (Array.isArray(enqData) ? enqData : []).map(e => ({
        ...e,
        followups: fuArr.filter(f => f.fkenquiryId === e.enquiryId),
      }));
      setAllData(withFollowups);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Filtering ─────────────────────────────────────────────────────────
  const filtered = allData.filter(enq => {
    const createdById = enq?.createdBy ?? enq?.fkCreatedBy ?? enq?.createdById;
    if (createdByFilter !== null && createdById !== createdByFilter) return false;

    const latestFu = enq.followups?.[0] || null;
    const assignedId = latestFu?.assignTo ?? enq?.assignTo ?? enq?.fkAssignTo ?? enq?.assignedTo;
    if (assignedToFilter !== null && assignedId !== assignedToFilter) return false;

    if (statusFilter !== "All") {
      const statusId   = latestFu?.fkLeadStatusId;
      const statusName = statusId ? (lsMap[statusId] || "") : "";
      const sn = statusName.toLowerCase();
      if (statusFilter === "New"           && (latestFu && statusId)) return false;
      if (statusFilter === "Follow"        && !sn.includes("follow"))  return false;
      if (statusFilter === "Success Close" && !sn.includes("success")) return false;
      if (statusFilter === "Cancel"        && !sn.includes("cancel"))  return false;
    }

    if (propertyFilter !== "All") {
      if ((enq.propertyType?.propertytypeName || "") !== propertyFilter) return false;
    }

    if (sourceFilter !== "All") {
      if ((enq.source?.sourceName || "") !== sourceFilter) return false;
    }

    if (selectedAreas.length > 0) {
      const rowAreaIds = getAreaIds(enq);
      if (!rowAreaIds.some(id => selectedAreas.includes(id))) return false;
    }

    const dateField = enq?.enquiryDate ?? enq?.createdAt ?? enq?.date;
    if (startDate || endDate) {
      const d = dateField ? new Date(dateField) : null;
      if (!d) return false;
      if (startDate && d < new Date(startDate))             return false;
      if (endDate   && d > new Date(endDate + "T23:59:59")) return false;
    }

    if (searchQuery.trim()) {
      const q           = searchQuery.trim().toLowerCase();
      const customer    = enq?.customer || {};
      const customerName = (customer.customerName || "").toLowerCase();
      const mobile      = (customer.mobileno || "").toLowerCase();
      const enquiryId   = String(enq?.enquiryId ?? enq?.id ?? "");
      const areaLbl     = (getAreaLabel(enq) || "").toLowerCase();
      const sourceName  = (enq?.source?.sourceName || "").toLowerCase();
      const propName    = (enq?.propertyType?.propertytypeName || "").toLowerCase();
      const createdByNm = (empMap[createdById]?.name || "").toLowerCase();
      const assignedNm  = (empMap[assignedId]?.name  || "").toLowerCase();
      const note        = (enq?.note || "").toLowerCase();
      const statusId    = latestFu?.fkLeadStatusId;
      const statusNm    = statusId ? (lsMap[statusId] || "").toLowerCase() : "";
      if (
        !customerName.includes(q) && !mobile.includes(q) &&
        !enquiryId.includes(q)   && !areaLbl.includes(q) &&
        !sourceName.includes(q)  && !propName.includes(q) &&
        !createdByNm.includes(q) && !assignedNm.includes(q) &&
        !note.includes(q)        && !statusNm.includes(q)
      ) return false;
    }

    return true;
  });

  useEffect(() => { setPage(1); }, [createdByFilter, assignedToFilter, selectedAreas, statusFilter, propertyFilter, sourceFilter, startDate, endDate, searchQuery]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated  = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  // ── Stats ─────────────────────────────────────────────────────────────
  const statsFollow  = allData.filter(e => { const s = lsMap[e.followups?.[0]?.fkLeadStatusId] || ""; return s.toLowerCase().includes("follow"); }).length;
  const statsSuccess = allData.filter(e => { const s = lsMap[e.followups?.[0]?.fkLeadStatusId] || ""; return s.toLowerCase().includes("success"); }).length;
  const statsCancel  = allData.filter(e => { const s = lsMap[e.followups?.[0]?.fkLeadStatusId] || ""; return s.toLowerCase().includes("cancel"); }).length;
  const statsNew     = allData.length - statsFollow - statsSuccess - statsCancel;

  const clearFilters = () => {
    setCreatedByFilter(null); setAssignedToFilter(null); setSelectedAreas([]);
    setStatusFilter("All"); setPropertyFilter("All"); setSourceFilter("All");
    setStartDate(""); setEndDate(""); setSearchQuery("");
  };
  const isFiltered = createdByFilter !== null || assignedToFilter !== null ||
    selectedAreas.length > 0 || statusFilter !== "All" || propertyFilter !== "All" ||
    sourceFilter !== "All" || startDate || endDate || searchQuery.trim();

  // ── Excel Export ──────────────────────────────────────────────────────
  const exportToExcel = () => {
    if (filtered.length === 0) return;
    setExporting(true);

    try {
      // ── Sheet 1: Enquiry Data ──
      const enquiryRows = filtered.map((enq, idx) => {
        const c           = enq?.customer || {};
        const latestFu    = enq.followups?.[0] || null;
        const statusId    = latestFu?.fkLeadStatusId;
        const statusName  = statusId ? (lsMap[statusId] || "New") : "New";
        const assignId    = latestFu?.assignTo;
        const assignName  = assignId ? (empMap[assignId]?.name || `Emp #${assignId}`) : "—";
        const createdById = enq?.createdBy ?? enq?.fkCreatedBy ?? enq?.createdById;
        const createdByNm = createdById ? (empMap[createdById]?.name || `#${createdById}`) : "—";
        const dateField   = enq?.enquiryDate ?? enq?.createdAt ?? enq?.date;

        return {
          "Sr No":            idx + 1,
          "Enquiry ID":       enq.enquiryId || "—",
          "Enquiry Date":     dateField ? new Date(dateField).toLocaleDateString("en-IN") : "—",
          "Customer Name":    c.customerName || "—",
          "Mobile":           c.mobileno || "—",
          "Email":            c.emailid || "—",
          "City":             c.city || "—",
          "Source":           enq.source?.sourceName || "—",
          "Property Type":    enq.propertyType?.propertytypeName || "—",
          "Budget":           enq.budget?.budgetName || "—",
          "Primary Goal":     enq.primaryGoal?.primarygoalName || "—",
          "Timeline":         enq.timeline?.timelineName || "—",
          "Areas":            enq.areas?.map(a => a.areaName).join(", ") || "—",
          "Lead Status":      statusName,
          "Assigned To":      assignName,
          "Created By":       createdByNm,
          "Employee Ref.":    enq.employee?.employeeName || "—",
          "Comm. Media":      enq.communicationMedia?.communicationmediaName || "—",
          "Followup Date":    latestFu?.callLeadDate ? new Date(latestFu.callLeadDate).toLocaleDateString("en-IN") : "—",
          "Followup Time":    latestFu?.callLeadTime || "—",
          "Followup Remark":  latestFu?.followupRemark || "—",
          "Note":             enq.note || "—",
        };
      });

      const ws1 = XLSX.utils.json_to_sheet(enquiryRows);

      // Column widths for Sheet 1
      ws1["!cols"] = [
        { wch: 6 },  // Sr No
        { wch: 12 }, // Enquiry ID
        { wch: 14 }, // Date
        { wch: 24 }, // Customer Name
        { wch: 14 }, // Mobile
        { wch: 26 }, // Email
        { wch: 14 }, // City
        { wch: 16 }, // Source
        { wch: 16 }, // Property Type
        { wch: 16 }, // Budget
        { wch: 18 }, // Primary Goal
        { wch: 14 }, // Timeline
        { wch: 26 }, // Areas
        { wch: 18 }, // Lead Status
        { wch: 20 }, // Assigned To
        { wch: 20 }, // Created By
        { wch: 18 }, // Employee Ref
        { wch: 16 }, // Comm. Media
        { wch: 14 }, // Followup Date
        { wch: 12 }, // Followup Time
        { wch: 30 }, // Followup Remark
        { wch: 30 }, // Note
      ];

      // Header row bold style using cell refs
      const headerRange = XLSX.utils.decode_range(ws1["!ref"]);
      for (let C = headerRange.s.c; C <= headerRange.e.c; C++) {
        const cellAddr = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!ws1[cellAddr]) continue;
        ws1[cellAddr].s = {
          font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
          fill: { fgColor: { rgb: "072877" } },
          alignment: { horizontal: "center", vertical: "center", wrapText: true },
        };
      }

      // ── Sheet 2: Follow-up History (all followups flat) ──
      const followupRows = [];
      filtered.forEach(enq => {
        const c = enq?.customer || {};
        const dateField = enq?.enquiryDate ?? enq?.createdAt ?? enq?.date;
        (enq.followups || []).forEach((fu, i) => {
          const fuStatus   = fu.fkLeadStatusId ? (lsMap[fu.fkLeadStatusId] || "—") : "—";
          const fuAssign   = fu.assignTo ? (empMap[fu.assignTo]?.name || `Emp #${fu.assignTo}`) : "—";
          followupRows.push({
            "Enquiry ID":       enq.enquiryId || "—",
            "Enquiry Date":     dateField ? new Date(dateField).toLocaleDateString("en-IN") : "—",
            "Customer Name":    c.customerName || "—",
            "Mobile":           c.mobileno || "—",
            "Followup #":       i + 1,
            "Followup Date":    fu.callLeadDate ? new Date(fu.callLeadDate).toLocaleDateString("en-IN") : "—",
            "Followup Time":    fu.callLeadTime || "—",
            "Status":           fuStatus,
            "Assigned To":      fuAssign,
            "Remark":           fu.followupRemark || "—",
          });
        });
      });

      const ws2 = XLSX.utils.json_to_sheet(followupRows.length > 0 ? followupRows : [{ "Info": "No followup data found" }]);
      ws2["!cols"] = [
        { wch: 12 }, { wch: 14 }, { wch: 24 }, { wch: 14 },
        { wch: 10 }, { wch: 14 }, { wch: 12 }, { wch: 18 },
        { wch: 20 }, { wch: 34 },
      ];

      // Header bold for Sheet 2
      if (followupRows.length > 0) {
        const range2 = XLSX.utils.decode_range(ws2["!ref"]);
        for (let C = range2.s.c; C <= range2.e.c; C++) {
          const cellAddr = XLSX.utils.encode_cell({ r: 0, c: C });
          if (!ws2[cellAddr]) continue;
          ws2[cellAddr].s = {
            font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
            fill: { fgColor: { rgb: "1D4ED8" } },
            alignment: { horizontal: "center", vertical: "center" },
          };
        }
      }

      // ── Build workbook ──
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws1, "Enquiry Report");
      XLSX.utils.book_append_sheet(wb, ws2, "Followup History");

      // ── Summary Sheet ──
      const summaryData = [
        { "Summary": "Total Enquiries",    "Count": filtered.length },
        { "Summary": "New",                "Count": filtered.filter(e => { const s = lsMap[e.followups?.[0]?.fkLeadStatusId] || ""; return !e.followups?.[0]?.fkLeadStatusId; }).length },
        { "Summary": "Follow-up",          "Count": filtered.filter(e => { const s = lsMap[e.followups?.[0]?.fkLeadStatusId] || ""; return s.toLowerCase().includes("follow"); }).length },
        { "Summary": "Success / Closed",   "Count": filtered.filter(e => { const s = lsMap[e.followups?.[0]?.fkLeadStatusId] || ""; return s.toLowerCase().includes("success"); }).length },
        { "Summary": "Cancelled",          "Count": filtered.filter(e => { const s = lsMap[e.followups?.[0]?.fkLeadStatusId] || ""; return s.toLowerCase().includes("cancel"); }).length },
        { "Summary": "Export Date",        "Count": new Date().toLocaleDateString("en-IN") },
        { "Summary": "Filters Applied",    "Count": isFiltered ? "Yes" : "No" },
      ];
      const ws3 = XLSX.utils.json_to_sheet(summaryData);
      ws3["!cols"] = [{ wch: 24 }, { wch: 14 }];
      XLSX.utils.book_append_sheet(wb, ws3, "Summary");

      // ── Download ──
      const today = new Date().toLocaleDateString("en-IN").replace(/\//g, "-");
      const fileName = `Enquiry_Report_${today}.xlsx`;
      XLSX.writeFile(wb, fileName);

    } catch (err) {
      console.error("Export error:", err);
      alert("Export failed: " + err.message);
    } finally {
      setExporting(false);
    }
  };

  const uniquePropTypes = [
    ...new Map(
      allData.filter(e => e.propertyType?.propertytypeName)
             .map(e => [e.propertyType.propertytypeId, e.propertyType])
    ).values()
  ];
  const uniqueSources = [
    ...new Map(
      allData.filter(e => e.source?.sourceName)
             .map(e => [e.source.sourceId, e.source])
    ).values()
  ];

  const statusOptions = ["All", "New", "Follow", "Success Close", "Cancel"];

  return (
    <div style={{ width: "100%", minHeight: "100vh", background: "#F1F5F9", fontFamily: "'Poppins', sans-serif", paddingBottom: 40 }}>
      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes fadeIn  { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        .rpt-row { transition: background .15s; cursor: pointer; }
        .rpt-row:hover { background: #EFF6FF !important; }
        .date-input {
          height: 40px; border: 1.5px solid #E2E8F0; border-radius: 8px;
          padding: 0 10px; font-size: 13px; font-family: 'Poppins', sans-serif;
          color: #1E293B; outline: none; background: #fff; cursor: pointer; width: 148px;
        }
        .date-input:focus { border-color: #1D4ED8; }
        .page-btn {
          padding: 6px 14px; border-radius: 6px; border: 1.5px solid #E2E8F0;
          background: #fff; cursor: pointer; font-size: 13px; font-weight: 600;
          color: #1D4ED8; font-family: 'Poppins', sans-serif; transition: all .15s;
        }
        .page-btn:disabled { color: #CBD5E1; cursor: not-allowed; background: #F8FAFC; }
        .page-btn.curr { background: #1D4ED8; color: #fff; border-color: #1D4ED8; }
        .sel-filter {
          height: 42px; border: 1.5px solid #E2E8F0; border-radius: 8px;
          padding: 0 32px 0 12px; font-size: 13px; font-family: 'Poppins', sans-serif;
          color: #64748B; outline: none; background: #fff; cursor: pointer;
          appearance: none; -webkit-appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: right 10px center;
          min-width: 150px; font-weight: 500;
        }
        .sel-filter:focus { border-color: #1D4ED8; }
        .sel-filter.active { border-color: #1D4ED8; color: #1D4ED8; font-weight: 700; background-color: #EFF6FF; }
        .export-btn {
          background: linear-gradient(135deg, #16A34A, #15803D);
          border: none; color: #fff; padding: 8px 20px; border-radius: 50px;
          font-weight: 700; font-size: 13px; cursor: pointer;
          font-family: 'Poppins', sans-serif;
          display: flex; align-items: center; gap: 7px;
          box-shadow: 0 2px 8px rgba(22,163,74,0.35);
          transition: all .2s; white-space: nowrap;
        }
        .export-btn:hover { background: linear-gradient(135deg, #15803D, #166534); transform: translateY(-1px); box-shadow: 0 4px 14px rgba(22,163,74,0.45); }
        .export-btn:active { transform: translateY(0); }
        .export-btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
        @media (max-width: 640px) { .filter-bar { flex-direction: column !important; } }
      `}</style>

      {/* ── Header ── */}
      <div style={{
        background: "#0d1b3e",
        backgroundImage: `repeating-linear-gradient(0deg,transparent,transparent 39px,rgba(255,255,255,0.018) 39px,rgba(255,255,255,0.018) 40px),
          repeating-linear-gradient(90deg,transparent,transparent 39px,rgba(255,255,255,0.018) 39px,rgba(255,255,255,0.018) 40px)`,
        padding: "0 28px", display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 16, minHeight: 76,
      }}>
        <div style={{ padding: "14px 0" }}>
          <div style={{ fontSize: 9.5, fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase", color: "rgba(148,163,184,0.65)", marginBottom: 3 }}>LEAD MANAGEMENT</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#fff" }}>📋 Enquiry Report</div>
        </div>

        {/* Header Buttons */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {/* ── Export Excel Button ── */}
          {!loading && !error && filtered.length > 0 && (
            <button
              className="export-btn"
              onClick={exportToExcel}
              disabled={exporting}
            >
              {exporting
                ? <><span style={{ fontSize: 14 }}>⏳</span> Exporting...</>
                : <><span style={{ fontSize: 14 }}>📥</span> Export Excel <span style={{ background: "rgba(255,255,255,0.25)", borderRadius: 20, padding: "1px 8px", fontSize: 11.5, fontWeight: 800 }}>{filtered.length}</span></>
              }
            </button>
          )}

          {/* Refresh Button */}
          <button
            onClick={fetchAll}
            style={{ background: "rgba(255,255,255,.1)", border: "1px solid rgba(255,255,255,.2)", color: "#fff", padding: "8px 18px", borderRadius: 50, fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "'Poppins', sans-serif", display: "flex", alignItems: "center", gap: 6 }}
          >🔄 Refresh</button>
        </div>
      </div>

      <div style={{ padding: "18px 16px" }}>

        {/* ── Filter Bar ── */}
        <div className="filter-bar" style={{ background: "#fff", borderRadius: 12, padding: "14px 16px", marginBottom: 12, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end", boxShadow: "0 1px 6px rgba(7,40,119,.07)" }}>

          {/* Search */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#374151", letterSpacing: "0.5px", textTransform: "uppercase" }}>Search</label>
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <span style={{ position: "absolute", left: 10, fontSize: 14, color: "#94A3B8", pointerEvents: "none" }}>🔍</span>
              <input
                type="text"
                placeholder="Name, mobile, note, area..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{
                  height: 42, border: `1.5px solid ${searchQuery.trim() ? BR : "#E2E8F0"}`, borderRadius: 8,
                  padding: "0 30px 0 32px", fontSize: 13, fontFamily: "'Poppins', sans-serif",
                  color: "#1E293B", outline: "none", background: "#fff", minWidth: 210,
                }}
              />
              {searchQuery && <button onClick={() => setSearchQuery("")} style={{ position: "absolute", right: 8, background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#94A3B8", padding: 0 }}>✕</button>}
            </div>
          </div>

          {/* Area */}
          <AreaMultiSelect areas={areaList} selectedIds={selectedAreas} onChange={setSelectedAreas} />

          {/* Created By */}
          <EmployeeDropdown label="Created By" employees={empList} selectedId={createdByFilter} onChange={setCreatedByFilter} />

          {/* Start Date */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#374151", letterSpacing: "0.5px", textTransform: "uppercase" }}>From Date</label>
            <input type="date" className="date-input" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ borderColor: startDate ? BR : "#E2E8F0" }} />
          </div>

          {/* End Date */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#374151", letterSpacing: "0.5px", textTransform: "uppercase" }}>To Date</label>
            <input type="date" className="date-input" value={endDate} onChange={e => setEndDate(e.target.value)} min={startDate || undefined} style={{ borderColor: endDate ? BR : "#E2E8F0" }} />
          </div>

          {/* Clear */}
          {isFiltered && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 11, color: "transparent" }}>x</label>
              <button onClick={clearFilters} style={{ height: 42, padding: "0 16px", borderRadius: 8, border: "1.5px solid #FECACA", background: "#FEF2F2", color: "#991B1B", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Poppins', sans-serif" }}>✕ Clear All</button>
            </div>
          )}

          <div style={{ marginLeft: "auto", fontSize: 13, color: "#64748B", fontWeight: 600, alignSelf: "flex-end", paddingBottom: 6 }}>
            {filtered.length} record{filtered.length !== 1 ? "s" : ""}
          </div>
        </div>

        {/* ── Active Filter Chips ── */}
        {isFiltered && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
            {createdByFilter !== null && (() => {
              const emp = empList.find(e => e.id === createdByFilter);
              return (
                <span style={{ background: "#EFF6FF", color: BR, border: "1px solid #BFDBFE", borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "'Poppins', sans-serif" }}>
                  ✏️ Created by: {emp?.name || `#${createdByFilter}`}
                  <button onClick={() => setCreatedByFilter(null)} style={{ background: "none", border: "none", cursor: "pointer", color: BR, fontSize: 12, padding: 0, fontWeight: 700 }}>✕</button>
                </span>
              );
            })()}
            {assignedToFilter !== null && (() => {
              const emp = empList.find(e => e.id === assignedToFilter);
              return (
                <span style={{ background: "#F0FDF4", color: "#166534", border: "1px solid #BBF7D0", borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "'Poppins', sans-serif" }}>
                  👤 Assigned to: {emp?.name || `#${assignedToFilter}`}
                  <button onClick={() => setAssignedToFilter(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#166534", fontSize: 12, padding: 0, fontWeight: 700 }}>✕</button>
                </span>
              );
            })()}
            {statusFilter !== "All" && (
              <span style={{ background: "#F5F3FF", color: "#5B21B6", border: "1px solid #DDD6FE", borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5, fontFamily: "'Poppins', sans-serif" }}>
                🏷️ Status: {statusFilter}
                <button onClick={() => setStatusFilter("All")} style={{ background: "none", border: "none", cursor: "pointer", color: "#5B21B6", fontSize: 12, padding: 0, fontWeight: 700 }}>✕</button>
              </span>
            )}
            {propertyFilter !== "All" && (
              <span style={{ background: "#EFF6FF", color: BR, border: "1px solid #BFDBFE", borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5, fontFamily: "'Poppins', sans-serif" }}>
                🏠 {propertyFilter}
                <button onClick={() => setPropertyFilter("All")} style={{ background: "none", border: "none", cursor: "pointer", color: BR, fontSize: 12, padding: 0, fontWeight: 700 }}>✕</button>
              </span>
            )}
            {sourceFilter !== "All" && (
              <span style={{ background: "#FFF7ED", color: "#9A3412", border: "1px solid #FED7AA", borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5, fontFamily: "'Poppins', sans-serif" }}>
                📣 {sourceFilter}
                <button onClick={() => setSourceFilter("All")} style={{ background: "none", border: "none", cursor: "pointer", color: "#9A3412", fontSize: 12, padding: 0, fontWeight: 700 }}>✕</button>
              </span>
            )}
            {selectedAreas.map(id => {
              const a = areaList.find(x => x.areaId === id);
              return (
                <span key={id} style={{ background: "#FFF7ED", color: "#9A3412", border: "1px solid #FED7AA", borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5, fontFamily: "'Poppins', sans-serif" }}>
                  📍 {a?.areaName || id}
                  <button onClick={() => setSelectedAreas(selectedAreas.filter(x => x !== id))} style={{ background: "none", border: "none", cursor: "pointer", color: "#9A3412", fontSize: 12, padding: 0, fontWeight: 700 }}>✕</button>
                </span>
              );
            })}
          </div>
        )}

        {/* ── Loading ── */}
        {loading && (
          <div style={{ background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 1px 6px rgba(7,40,119,.06)" }}>
            {[...Array(7)].map((_, i) => (
              <div key={i} style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16 }}>
                <Skeleton width={36} height={36} radius={50} />
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                  <Skeleton width="35%" height={13} />
                  <Skeleton width="55%" height={11} />
                </div>
                <Skeleton width={90} height={24} radius={20} />
                <Skeleton width={70} height={24} radius={20} />
              </div>
            ))}
          </div>
        )}

        {/* ── Error ── */}
        {error && !loading && (
          <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 12, padding: 24, textAlign: "center", color: "#991B1B" }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>⚠️</div>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>API Error</div>
            <div style={{ fontSize: 13, opacity: .8, marginBottom: 14 }}>{error}</div>
            <button onClick={fetchAll} style={{ padding: "8px 22px", borderRadius: 50, background: "#DC2626", color: "#fff", border: "none", fontWeight: 700, cursor: "pointer", fontFamily: "'Poppins', sans-serif" }}>🔄 Retry</button>
          </div>
        )}

        {/* ── Table ── */}
        {!loading && !error && (
          <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 2px 12px rgba(7,40,119,.07)", overflow: "hidden", animation: "fadeIn .3s ease" }}>
            {paginated.length === 0 ? (
              <div style={{ padding: 52, textAlign: "center", color: "#94A3B8" }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
                <div style={{ fontWeight: 700, fontSize: 16, color: "#64748B" }}>No enquiries found</div>
                <div style={{ fontSize: 13, marginTop: 4 }}>Try changing the filters or search query</div>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1300 }}>
                  <thead>
                    <tr style={{ background: DK, borderBottom: `2px solid ${BR}` }}>
                      {["#", "Customer", "Date", "Source", "Property", "Budget", "Areas", "Lead Status", "Created By", "Note"].map(h => (
                        <th key={h} style={{
                          padding: "12px 12px",
                          textAlign: "left",
                          fontSize: 11.5,
                          fontWeight: 700,
                          color: "#ffffff",
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                          whiteSpace: "nowrap",
                          fontFamily: "'Poppins', sans-serif",
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((enq, idx) => {
                      const srNo       = (page - 1) * PER_PAGE + idx + 1;
                      const c          = enq?.customer || {};
                      const areas      = enq.areas?.map(a => a.areaName).join(", ") || "—";
                      const latestFu   = enq.followups?.[0] || null;
                      const statusId   = latestFu?.fkLeadStatusId;
                      const statusName = statusId ? (lsMap[statusId] || "New") : "New";
                      const assignId   = latestFu?.assignTo;
                      const assignName = assignId ? (empMap[assignId]?.name || `Emp #${assignId}`) : null;
                      const createdById = enq?.createdBy ?? enq?.fkCreatedBy ?? enq?.createdById;
                      const createdByNm = createdById ? (empMap[createdById]?.name || `#${createdById}`) : null;
                      const dateField  = enq?.enquiryDate ?? enq?.createdAt ?? enq?.date;

                      return (
                        <tr
                          key={enq?.enquiryId ?? enq?.id ?? idx}
                          className="rpt-row"
                          style={{ borderBottom: "1px solid #F1F5F9" }}
                          onClick={() => setSelectedEnquiry(enq)}
                          title="Click to view details"
                        >
                          {/* # */}
                          <td style={{ padding: "10px 12px", fontSize: 13, color: "#94A3B8", fontWeight: 600 }}>{enq.enquiryId || srNo}</td>

                          {/* Customer */}
                          <td style={{ padding: "10px 12px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <Avatar name={c.customerName} size={32} />
                              <div>
                                <div style={{ fontWeight: 700, fontSize: 13.5, color: "#1E293B" }}>{c.customerName || "—"}</div>
                                <div style={{ fontSize: 11.5, color: "#94A3B8" }}>{c.mobileno || ""}</div>
                              </div>
                            </div>
                          </td>

                          {/* Date */}
                          <td style={{ padding: "10px 12px", fontSize: 13, color: "#475569", whiteSpace: "nowrap" }}>{formatDate(dateField)}</td>

                          {/* Source */}
                          <td style={{ padding: "10px 12px", fontSize: 13, color: "#475569" }}>{enq.source?.sourceName || "—"}</td>

                          {/* Property */}
                          <td style={{ padding: "10px 12px" }}>
                            {enq.propertyType?.propertytypeName ? (
                              <span style={{ background: "#EFF6FF", color: BR, padding: "2px 8px", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                                {enq.propertyType.propertytypeName}
                              </span>
                            ) : <span style={{ color: "#CBD5E1", fontSize: 12 }}>—</span>}
                          </td>

                          {/* Budget */}
                          <td style={{ padding: "10px 12px", fontSize: 13, color: "#475569", whiteSpace: "nowrap" }}>
                            {enq.budget?.budgetName
                              ? <span style={{ background: "#F0FDF4", color: "#166534", padding: "2px 8px", borderRadius: 6, fontSize: 12, fontWeight: 700 }}>💰 {enq.budget.budgetName}</span>
                              : <span style={{ color: "#CBD5E1", fontSize: 12 }}>—</span>}
                          </td>

                          {/* Areas */}
                          <td style={{ padding: "10px 12px", maxWidth: 140 }}>
                            {areas !== "—"
                              ? <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12.5, color: "#475569" }} title={areas}>📍 {areas}</div>
                              : <span style={{ color: "#CBD5E1", fontSize: 12 }}>—</span>}
                          </td>

                          {/* Lead Status */}
                          <td style={{ padding: "10px 12px" }}>
                            <StatusBadge statusName={statusName} />
                          </td>

                          {/* Created By */}
                          <td style={{ padding: "10px 12px" }}>
                            {createdByNm ? (
                              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                <Avatar name={createdByNm} size={26} />
                                <span style={{ fontWeight: 600, color: "#1E293B", fontSize: 12.5, whiteSpace: "nowrap" }}>{createdByNm}</span>
                              </div>
                            ) : <span style={{ color: "#CBD5E1", fontSize: 12 }}>—</span>}
                          </td>

                          {/* Note */}
                          <td style={{ padding: "10px 12px", maxWidth: 160 }}>
                            {enq.note
                              ? <span title={enq.note} style={{ display: "block", fontSize: 12.5, color: "#475569", fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 150 }}>💬 {enq.note}</span>
                              : <span style={{ color: "#CBD5E1", fontSize: 12 }}>—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ borderTop: "1px solid #F1F5F9", padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                <span style={{ fontSize: 13, color: "#64748B" }}>
                  Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, filtered.length)} of {filtered.length}
                </span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button className="page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>← Prev</button>
                  {[...Array(totalPages)].map((_, i) =>
                    Math.abs(page - (i + 1)) <= 2 && (
                      <button key={i} className={`page-btn${page === i + 1 ? " curr" : ""}`} onClick={() => setPage(i + 1)}>{i + 1}</button>
                    )
                  )}
                  <button className="page-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next →</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Enquiry Detail Modal ── */}
      {selectedEnquiry && (
        <EnquiryModal
          enquiry={selectedEnquiry}
          onClose={() => setSelectedEnquiry(null)}
          empMap={empMap}
          lsMap={lsMap}
        />
      )}
    </div>
  );
}
